import { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { apiClient } from "@/lib/api";
import { MotionReveal } from "@/components/shared/MotionReveal";
import { Watermark } from "@/components/shared/Watermark";
import { CTAButton } from "@/components/shared/CTAButton";
import { useLanguage } from "@/i18n/LanguageContext";

const GOOGLE_REVIEWS_URL = "https://share.google/ON3943nfkhBAMzodC";

const initials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const TestimonialsCarousel = () => {
  const [items, setItems] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    apiClient.get("/testimonials").then((res) => setItems(res.data)).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 bg-white overflow-hidden" data-testid="testimonials-section">
      <Watermark word="Trust" position="top-left" className="text-[color:var(--navy-950)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <MotionReveal className="flex flex-col items-center text-center mb-12">
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("testimonials.eyebrow")}</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[color:var(--navy-950)]">
            {t("testimonials.heading1")} <span className="italic text-[color:var(--gold-500)]">{t("testimonials.headingEmphasis")}</span> {t("testimonials.headingEnd")}
          </h2>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6"
            data-testid="google-reviews-link"
          >
            <CTAButton variant="outlineLight" icon={Star}>
              {t("testimonials.googleCta")}
            </CTAButton>
          </a>
        </MotionReveal>

        <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="testimonials-carousel">
          <CarouselContent>
            {items.map((item) => (
              <CarouselItem key={item.id} className="sm:basis-1/2 lg:basis-1/2" data-testid="testimonial-slide">
                <div className="h-full rounded-3xl border border-slate-200 bg-[color:var(--sand)] p-7 sm:p-8 shadow-[0_10px_30px_rgba(11,18,32,0.05)] flex flex-col">
                  <Quote className="h-7 w-7 text-[color:var(--gold-500)] mb-4" />
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed flex-1">&ldquo;{item.quote}&rdquo;</p>
                  <div className="mt-6 pt-5 border-t border-slate-200 flex items-center gap-3">
                    <Avatar className="h-10 w-10 ring-2 ring-white shadow-sm">
                      <AvatarFallback className="bg-[color:var(--navy-950)] text-[color:var(--gold-400)] font-heading text-sm">
                        {initials(item.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold text-[color:var(--navy-950)]">{item.name}</p>
                      <span className="inline-block mt-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[color:var(--gold-500)]/12 text-[color:var(--navy-950)]">
                        {item.practice_area}
                      </span>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex items-center justify-center gap-3 mt-10">
            <CarouselPrevious className="static translate-y-0 h-10 w-10 border-slate-300 hover:bg-[color:var(--navy-950)] hover:text-white" data-testid="testimonials-prev-button" />
            <CarouselNext className="static translate-y-0 h-10 w-10 border-slate-300 hover:bg-[color:var(--navy-950)] hover:text-white" data-testid="testimonials-next-button" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};
